import { useState, useRef, useCallback, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Files, Search, GitBranch, User, Settings, FolderOpen,
  PlusSquare, Save, LogOut,
  Command, ChevronDown, ChevronRight, ArrowLeft, ArrowRight, Sidebar,
  PanelBottom, PanelRight, X, Square, Minus, History,
  XCircle, TriangleAlert, MessageCircle, Layers, Play
} from 'lucide-react'

import { FileExplorer } from '@/components/explorer/FileExplorer'
import { EditorTabs } from '@/components/editor/EditorTabs'
import { useIdeStore } from '@/store/ideStore'
import { terminalApi } from '@/api/ideApi'
import { windowApi } from '@/api/desktopBridge'
import { isElectron } from '@/utils/env'

import arkitectLogo from '../../assets/arkinavlogo.png'

const MIN_SIDEBAR_WIDTH = 160
const MAX_SIDEBAR_WIDTH = 600
const MIN_RIGHT_SIDEBAR_WIDTH = 260
const MAX_RIGHT_SIDEBAR_WIDTH = 680
const MIN_TERMINAL_HEIGHT = 80
const MAX_TERMINAL_HEIGHT = 500
const DEFAULT_SIDEBAR_WIDTH = 260
const DEFAULT_RIGHT_SIDEBAR_WIDTH = 340
const DEFAULT_TERMINAL_HEIGHT = 220

// ─── Search Panel ────────────────────────────────────────────────────────────

function SearchPanel() {
  const [query, setQuery] = useState('')
  const openFiles = useIdeStore(state => state.openFiles)

  const results = query.trim()
    ? openFiles.flatMap(file => {
        const lines = (file.contents || '').split('\n')
        return lines
          .map((line, idx) => ({ file: file.name, path: file.path, line: idx + 1, text: line }))
          .filter(r => r.text.toLowerCase().includes(query.toLowerCase()))
      })
    : []

  return (
    <div className="side-panel-body">
      <div className="search-input-wrap">
        <input
          className="search-input"
          placeholder="Search in open files…"
          value={query}
          onChange={e => setQuery(e.target.value)}
          autoFocus
        />
      </div>
      {query.trim() && results.length === 0 && (
        <div className="muted" style={{ padding: '12px 16px', fontSize: 'var(--font-size-sm)' }}>No results found.</div>
      )}
      {results.map((r, i) => (
        <div key={i} className="search-result">
          <div className="search-result-file">{r.file}:{r.line}</div>
          <div className="search-result-line">{r.text.trim()}</div>
        </div>
      ))}
    </div>
  )
}

// ─── SCM Panel ───────────────────────────────────────────────────────────────

function ScmPanel({ project }) {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const appendTerminalEntry = useIdeStore(state => state.appendTerminalEntry)

  const refresh = useCallback(async () => {
    if (!project?.path) {
      setStatus(null)
      return
    }
    setLoading(true)
    setError('')
    try {
      const result = await terminalApi.runCommand({ command: 'git status --short', cwd: project.path })
      setStatus(result.stdout || '(clean)')
      if (result.stderr && !result.stderr.toLowerCase().includes('not a git repository')) {
        setError(result.stderr)
      }
    } catch (err) {
      setError(String(err?.message || 'Failed to run git status'))
    } finally {
      setLoading(false)
    }
  }, [project?.path])

  useEffect(() => { refresh() }, [refresh])

  const onCommit = async (message) => {
    if (!project?.path || !message.trim()) return
    const result = await terminalApi.runCommand({
      command: `git add -A && git commit -m "${message.replace(/"/g, '\\"')}"`,
      cwd: project.path,
    })
    appendTerminalEntry({ command: 'git commit', stdout: result.stdout, stderr: result.stderr })
    await refresh()
  }

  return <ScmPanelView status={status} loading={loading} error={error} project={project} onRefresh={refresh} onCommit={onCommit} />
}

function ScmPanelView({ status, loading, error, project, onRefresh, onCommit }) {
  const [commitMsg, setCommitMsg] = useState('')

  if (!project) {
    return <div className="muted" style={{ padding: 12, fontSize: 'var(--font-size-sm)' }}>Open a project to view source control.</div>
  }

  return (
    <div className="side-panel-body">
      <div className="scm-toolbar">
        <button className="scm-btn" onClick={onRefresh} title="Refresh" disabled={loading}>↻ Refresh</button>
      </div>
      {error && <div className="scm-error">{error}</div>}
      <div className="scm-status">
        {loading ? <span className="muted" style={{ fontSize: 'var(--font-size-sm)' }}>Loading…</span> : (
          <pre className="scm-output">{status || '(clean)'}</pre>
        )}
      </div>
      <div className="scm-commit">
        <input
          className="scm-commit-input"
          placeholder="Commit message…"
          value={commitMsg}
          onChange={e => setCommitMsg(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && commitMsg.trim()) { onCommit(commitMsg); setCommitMsg('') } }}
        />
        <button
          className="scm-commit-btn"
          disabled={!commitMsg.trim()}
          onClick={() => { onCommit(commitMsg); setCommitMsg('') }}
        >
          Commit
        </button>
      </div>
    </div>
  )
}

// ─── Welcome Tab ─────────────────────────────────────────────────────────────

function WelcomeTab({ onOpenProject, onCreateProject }) {
  return (
    <div className="welcome-tab">
      <div className="welcome-logo">
        <img src={arkitectLogo} alt="Arkitect" />
      </div>
      <h2 className="welcome-title">Arkitect Desktop IDE</h2>
      <p className="welcome-subtitle">
        Build, test, and ship with an AI-powered development environment.
      </p>
      <div className="welcome-actions">
        <button className="welcome-action" onClick={onOpenProject}>
          <div className="welcome-action-icon">
            <FolderOpen size={16} />
          </div>
          <div>
            <div className="welcome-action-label">Open Project</div>
            <div className="welcome-action-desc">Open an existing project folder</div>
          </div>
        </button>
        <button className="welcome-action" onClick={onCreateProject}>
          <div className="welcome-action-icon">
            <PlusSquare size={16} />
          </div>
          <div>
            <div className="welcome-action-label">New Project</div>
            <div className="welcome-action-desc">Create a new project with AI assistance</div>
          </div>
        </button>
      </div>
      <div className="welcome-shortcuts">
        <span className="welcome-shortcut"><kbd>Ctrl+S</kbd> Save</span>
        <span className="welcome-shortcut"><kbd>Ctrl+B</kbd> Toggle sidebar</span>
        <span className="welcome-shortcut"><kbd>Ctrl+`</kbd> Toggle terminal</span>
        <span className="welcome-shortcut"><kbd>Ctrl+Shift+P</kbd> Commands</span>
      </div>
    </div>
  )
}

// ─── Breadcrumbs ─────────────────────────────────────────────────────────────

function Breadcrumbs({ filePath }) {
  if (!filePath) return null

  const separator = filePath.includes('\\') ? '\\' : '/'
  const parts = filePath.split(/[\\/]/)
  const displayed = parts.length > 4
    ? ['…', ...parts.slice(-3)]
    : parts

  return (
    <div className="editor-breadcrumbs">
      {displayed.map((part, i) => (
        <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {i > 0 && <ChevronRight size={10} style={{ color: 'var(--text-disabled)', flexShrink: 0 }} />}
          <span className="breadcrumb-item">{part}</span>
        </span>
      ))}
    </div>
  )
}

// ─── IdeLayout ────────────────────────────────────────────────────────────────

export function IdeLayout({
  session,
  governedRuntime,
  project,
  files,
  activeFile,
  onSelectFile,
  onCloseFile,
  onOpenTreeFile,
  onCreateProject,
  onOpenProject,
  onSaveFile,
  onLogin,
  onLogout,
  editor,
  assistant,
  terminal,
}) {
  const [activeView, setActiveView] = useState('explorer')
  const [isSidebarOpen, setIsSidebarOpen] = useState(true)
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true)
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH)
  const [rightSidebarWidth, setRightSidebarWidth] = useState(DEFAULT_RIGHT_SIDEBAR_WIDTH)
  const [terminalHeight, setTerminalHeight] = useState(DEFAULT_TERMINAL_HEIGHT)
  const [isTerminalOpen, setIsTerminalOpen] = useState(true)
  const [activePanelTab, setActivePanelTab] = useState('TERMINAL')

  const draggingSidebar = useRef(false)
  const draggingRightSidebar = useRef(false)
  const draggingTerminal = useRef(false)
  const dragStart = useRef({ x: 0, y: 0, initVal: 0 })

  const toggleView = (view) => {
    if (activeView === view) {
      setIsSidebarOpen(open => !open)
    } else {
      setActiveView(view)
      setIsSidebarOpen(true)
    }
  }

  const toggleAiPanel = () => setIsRightSidebarOpen(open => !open)
  const toggleTerminal = () => setIsTerminalOpen(open => !open)

  const openPanelTab = (tab) => {
    setActivePanelTab(tab)
    setIsTerminalOpen(true)
    if (terminalHeight < 100) setTerminalHeight(220)
  }

  // ── Drag handlers ───────────────────────────────────────────────────────
  const onSidebarDragStart = useCallback((e) => {
    e.preventDefault()
    draggingSidebar.current = true
    dragStart.current = { x: e.clientX, initVal: sidebarWidth }
  }, [sidebarWidth])

  const onTerminalDragStart = useCallback((e) => {
    e.preventDefault()
    draggingTerminal.current = true
    dragStart.current = { y: e.clientY, initVal: terminalHeight }
  }, [terminalHeight])

  const onRightSidebarDragStart = useCallback((e) => {
    e.preventDefault()
    draggingRightSidebar.current = true
    dragStart.current = { x: e.clientX, initVal: rightSidebarWidth }
  }, [rightSidebarWidth])

  useEffect(() => {
    const onMouseMove = (e) => {
      if (draggingSidebar.current) {
        const delta = e.clientX - dragStart.current.x
        const next = Math.min(MAX_SIDEBAR_WIDTH, Math.max(MIN_SIDEBAR_WIDTH, dragStart.current.initVal + delta))
        setSidebarWidth(next)
      }
      if (draggingRightSidebar.current) {
        const delta = dragStart.current.x - e.clientX
        const next = Math.min(MAX_RIGHT_SIDEBAR_WIDTH, Math.max(MIN_RIGHT_SIDEBAR_WIDTH, dragStart.current.initVal + delta))
        setRightSidebarWidth(next)
      }
      if (draggingTerminal.current) {
        const delta = dragStart.current.y - e.clientY
        const next = Math.min(MAX_TERMINAL_HEIGHT, Math.max(MIN_TERMINAL_HEIGHT, dragStart.current.initVal + delta))
        setTerminalHeight(next)
      }
    }
    const onMouseUp = () => {
      draggingSidebar.current = false
      draggingRightSidebar.current = false
      draggingTerminal.current = false
    }
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
    }
  }, [])

  // ── Keyboard shortcuts ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      // Ctrl+B: toggle sidebar
      if (e.ctrlKey && e.key === 'b') {
        e.preventDefault()
        setIsSidebarOpen(v => !v)
      }
      // Ctrl+`: toggle terminal
      if (e.ctrlKey && e.key === '`') {
        e.preventDefault()
        toggleTerminal()
      }
      // Ctrl+S: save
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault()
        onSaveFile()
      }
      // Ctrl+J: toggle bottom panel
      if (e.ctrlKey && e.key === 'j') {
        e.preventDefault()
        toggleTerminal()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onSaveFile])

  const userName = session?.user?.full_name || session?.user?.email || 'Guest'

  // File language for status bar
  const getLanguageLabel = () => {
    const ext = activeFile?.name?.split('.').pop()?.toLowerCase()
    const map = { js: 'JavaScript', jsx: 'React JSX', py: 'Python', html: 'HTML', css: 'CSS', json: 'JSON', md: 'Markdown', ts: 'TypeScript', tsx: 'React TSX', rs: 'Rust', go: 'Go', java: 'Java', rb: 'Ruby', sql: 'SQL', sh: 'Shell', yml: 'YAML', yaml: 'YAML' }
    return map[ext] || (ext ? ext.toUpperCase() : 'Plain Text')
  }

  return (
    <div className="vscode-shell">
      {/* ══════ Title Bar ══════ */}
      <div className="title-bar">
        <div className="title-bar-left">
          <div className="title-bar-icon" style={{ width: 24, height: 24, overflow: 'hidden', borderRadius: 'var(--radius-sm)' }}>
            <img src={arkitectLogo} alt="Arkitect" style={{ width: 18, height: 18 }} />
          </div>
          <div className="title-bar-menu">
            <span className="title-bar-menu-item">File</span>
            <span className="title-bar-menu-item">Edit</span>
            <span className="title-bar-menu-item">Selection</span>
            <span className="title-bar-menu-item">View</span>
            <span className="title-bar-menu-item">Go</span>
            <span className="title-bar-menu-item">Run</span>
            <span className="title-bar-menu-item">Terminal</span>
            <span className="title-bar-menu-item">Help</span>
          </div>
        </div>

        <div className="title-bar-center">
          <div className="title-bar-search">
            <Search size={13} />
            <span>{project?.name || 'Arkitect Desktop'}</span>
          </div>
        </div>

        <div className="title-bar-right">
          <div className="title-bar-controls">
            <div className="title-bar-icon" title="Back"><ArrowLeft size={15} /></div>
            <div className="title-bar-icon" title="Forward"><ArrowRight size={15} /></div>
          </div>
          <div className="title-bar-controls" style={{ borderLeft: '1px solid var(--border-subtle)', borderRight: '1px solid var(--border-subtle)', padding: '0 4px' }}>
            <div className="title-bar-icon" onClick={() => setIsSidebarOpen(!isSidebarOpen)} title="Toggle Primary Sidebar">
              <Sidebar size={15} style={{ color: isSidebarOpen ? 'var(--text-primary)' : undefined }} />
            </div>
            <div className="title-bar-icon" onClick={toggleTerminal} title="Toggle Panel">
              <PanelBottom size={15} style={{ color: isTerminalOpen ? 'var(--text-primary)' : undefined }} />
            </div>
            <div className="title-bar-icon" onClick={toggleAiPanel} title="Toggle AI Panel">
              <PanelRight size={15} style={{ color: isRightSidebarOpen ? 'var(--text-primary)' : undefined }} />
            </div>
          </div>
          <div className="title-bar-icon" title="Settings"><Settings size={15} /></div>
          {isElectron() && (
            <div className="title-bar-controls" style={{ gap: 0, marginLeft: 4 }}>
              <div className="title-bar-icon" onClick={() => windowApi.minimize()} title="Minimize"><Minus size={15} /></div>
              <div className="title-bar-icon" onClick={() => windowApi.toggleMaximize()} title="Maximize"><Square size={13} /></div>
              <div className="title-bar-icon close-icon" onClick={() => windowApi.close()} title="Close"><X size={15} /></div>
            </div>
          )}
        </div>
      </div>

      {/* ══════ Main Area ══════ */}
      <div className="vscode-main">

        {/* Activity Bar */}
        <div className="activity-bar">
          <div className="activity-actions top">
            <div className={`activity-item ${activeView === 'explorer' && isSidebarOpen ? 'active' : ''}`} onClick={() => toggleView('explorer')} title="Explorer">
              <Files size={22} strokeWidth={1.5} />
            </div>
            <div className={`activity-item ${activeView === 'search' && isSidebarOpen ? 'active' : ''}`} onClick={() => toggleView('search')} title="Search">
              <Search size={22} strokeWidth={1.5} />
            </div>
            <div className={`activity-item ${activeView === 'scm' && isSidebarOpen ? 'active' : ''}`} onClick={() => toggleView('scm')} title="Source Control">
              <GitBranch size={22} strokeWidth={1.5} />
            </div>
            <div className="activity-item" title="Run and Debug">
              <Play size={22} strokeWidth={1.5} />
            </div>
            <div className="activity-item" title="Extensions">
              <Layers size={22} strokeWidth={1.5} />
            </div>
          </div>
          <div className="activity-actions bottom">
            <div
              className="activity-item"
              title={session?.authenticated ? userName : 'Sign In'}
              onClick={session?.authenticated ? undefined : onLogin}
            >
              <User size={22} strokeWidth={1.5} style={{ color: session?.authenticated ? 'var(--accent-color)' : undefined }} />
            </div>
            <div className="activity-item" title={session?.authenticated ? 'Logout' : 'Settings'} onClick={session?.authenticated ? onLogout : undefined}>
              {session?.authenticated ? <LogOut size={22} strokeWidth={1.5} /> : <Settings size={22} strokeWidth={1.5} />}
            </div>
          </div>
        </div>

        {/* Primary Side Bar */}
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: sidebarWidth, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: 'spring', bounce: 0.12, duration: 0.35 }}
              style={{ display: 'flex', minWidth: MIN_SIDEBAR_WIDTH, flexShrink: 0 }}
            >
              <div className="side-bar" style={{ width: '100%' }}>
                {activeView === 'explorer' && (
                  <>
                    <div className="side-bar-header">
                      <span>Explorer</span>
                      <span style={{ cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>⋯</span>
                    </div>
                    <div className="side-bar-actions">
                      <button onClick={onOpenProject} title="Open Project"><FolderOpen size={14} /></button>
                      <button onClick={onCreateProject} title="Create Project"><PlusSquare size={14} /></button>
                      <button onClick={onSaveFile} title="Save File"><Save size={14} /></button>
                    </div>
                    <div className="side-bar-content" style={{ padding: '0' }}>
                      <FileExplorer project={project} onOpenFile={onOpenTreeFile} activeFilePath={activeFile?.path} />
                    </div>
                    <div className="explorer-sections">
                      <div className="explorer-section-header"><ChevronRight size={12} /> Outline</div>
                      <div className="explorer-section-header"><ChevronRight size={12} /> Timeline</div>
                    </div>
                  </>
                )}
                {activeView === 'search' && (
                  <>
                    <div className="side-bar-header"><span>Search</span></div>
                    <SearchPanel />
                  </>
                )}
                {activeView === 'scm' && (
                  <>
                    <div className="side-bar-header"><span>Source Control</span></div>
                    <ScmPanel project={project} />
                  </>
                )}
              </div>
              <div className="resize-handle resize-handle--vertical" onMouseDown={onSidebarDragStart} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Editor & Panel Area */}
        <div className="editor-area-container">
          <div className="editor-area">
            <EditorTabs
              files={files}
              activeFilePath={activeFile?.path || null}
              onSelect={onSelectFile}
              onClose={onCloseFile}
            />
            {activeFile && <Breadcrumbs filePath={activeFile.path} />}
            <div className="editor-stage">
              {activeFile ? editor : <WelcomeTab onOpenProject={onOpenProject} onCreateProject={onCreateProject} />}
            </div>
          </div>

          {/* Terminal */}
          {isTerminalOpen && (
            <>
              <div className="resize-handle resize-handle--horizontal" onMouseDown={onTerminalDragStart} />
              <div className="bottom-panel" style={{ height: terminalHeight }}>
                <div className="panel-tabs">
                  <div className={`panel-tab ${activePanelTab === 'TERMINAL' ? 'active' : ''}`} onClick={() => openPanelTab('TERMINAL')}>Terminal</div>
                  <div className={`panel-tab ${activePanelTab === 'OUTPUT' ? 'active' : ''}`} onClick={() => openPanelTab('OUTPUT')}>Output</div>
                  <div className={`panel-tab ${activePanelTab === 'PROBLEMS' ? 'active' : ''}`} onClick={() => openPanelTab('PROBLEMS')}>Problems</div>
                  <div className={`panel-tab ${activePanelTab === 'DEBUG' ? 'active' : ''}`} onClick={() => openPanelTab('DEBUG')}>Debug Console</div>
                </div>
                <div className="panel-content">
                  {activePanelTab === 'TERMINAL' ? terminal : <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>No {activePanelTab.toLowerCase()} output.</div>}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Right Side Bar (AI) */}
        <AnimatePresence>
          {isRightSidebarOpen && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: rightSidebarWidth, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ type: 'spring', bounce: 0.12, duration: 0.35 }}
              style={{ display: 'flex', minWidth: MIN_RIGHT_SIDEBAR_WIDTH, flexShrink: 0 }}
            >
              <div className="resize-handle resize-handle--vertical" onMouseDown={onRightSidebarDragStart} />
              <div className="side-bar right-side-bar" style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <div className="side-bar-header" style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 700, color: 'var(--text-primary)', fontSize: 'var(--font-size-md)' }}>
                  <span>Arkitect AI</span>
                  <div className="ai-header-actions">
                    <div className="title-bar-icon" title="New Chat"><PlusSquare size={14} /></div>
                    <div className="title-bar-icon" title="History"><History size={14} /></div>
                    <div className="title-bar-icon" title="Close" onClick={toggleAiPanel}><X size={14} /></div>
                  </div>
                </div>
                <div className="side-bar-content ai-panel-container" style={{ flex: 1, overflow: 'hidden', padding: 0 }}>
                  {assistant}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ══════ Status Bar ══════ */}
      <div className="status-bar">
        <div className="status-left">
          {governedRuntime?.taskContext && (
            <span className="status-item" title={`Plan v${governedRuntime.taskContext.plan_version} · ${governedRuntime.taskContext.policy_version}`}>
              <Play size={13} /> {governedRuntime.taskContext.task_code}: {governedRuntime.taskContext.task_title}
            </span>
          )}
          <span className="status-item" onClick={() => openPanelTab('PROBLEMS')} title="Errors">
            <XCircle size={13} /> 0
          </span>
          <span className="status-item" onClick={() => openPanelTab('PROBLEMS')} title="Warnings">
            <TriangleAlert size={13} /> 0
          </span>
        </div>
        <div className="status-right">
          {activeFile && (
            <>
              <span className="status-item" title="Encoding">UTF-8</span>
              <span className="status-item" title="End of Line">LF</span>
              <span className="status-item" title="Language">{getLanguageLabel()}</span>
            </>
          )}
          <span className="status-item" title="Toggle AI Assistant" onClick={toggleAiPanel}>
            <Command size={13} /> AI
          </span>
        </div>
      </div>
    </div>
  )
}
